import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogCloseTrigger,
} from "../ui/dialog";
import {
  Button,
  VStack,
  HStack,
  Box,
  Icon,
  Text,
  Grid,
  Input,
  Select,
  createListCollection,
} from "@chakra-ui/react";
import { Field } from "../ui/field";
import { useState, useEffect } from "react";
import {
  FiFileText,
  FiHash,
  FiCalendar,
  FiList,
  FiUpload,
} from "react-icons/fi";
import type { FieldType } from "../../client";
import type { SelectedField } from "./AddTableFieldFromSchema"; // 또는 올바른 경로

interface TempFieldEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  field: SelectedField;
  onSave: (updated: SelectedField) => void;
}

const fieldTypes = [
  { type: "string", icon: FiFileText, label: "문자열" },
  { type: "number", icon: FiHash, label: "숫자" },
  { type: "datetime", icon: FiCalendar, label: "날짜/시간" },
  { type: "selection", icon: FiList, label: "선택" },
  { type: "file", icon: FiUpload, label: "파일" },
];

const TempFieldEditDialog = ({
  isOpen,
  onClose,
  field,
  onSave,
}: TempFieldEditDialogProps) => {
  const [localField, setLocalField] = useState(field);
  const [selectedFieldType, setSelectedFieldType] = useState<FieldType>(
    field.field_type
  );
  const [numberType, setNumberType] = useState(
    localField.number_metadata?.number_type || "natural"
  );
  const [stringType, setStringType] = useState(
    localField.string_metadata?.string_type || "shortText"
  );
  const [datetimeType, setDatetimeType] = useState(
    localField.datetime_metadata?.datetime_type || "datetime"
  );
  const [selectionOptions, setSelectionOptions] = useState<
    Array<{ label: string; color: string; id?: string }>
  >(
    (field.selection_metadata?.options || []).map(
      (opt: { [key: string]: unknown }) => ({
        label: String(opt.label || ""),
        color: String(opt.color || "#000000"),
        id: String(opt.id || `option-${Date.now()}-${Math.random()}`),
      })
    )
  );
  const [fileExt, setFileExt] = useState(
    localField.file_metadata?.allowed_extension || "pdf"
  );
  const [fileSize, setFileSize] = useState(
    localField.file_metadata?.max_file_size_mb || 10
  );

  const numberTypeCollection = createListCollection({
    items: [
      { label: "자연수", value: "natural" },
      { label: "정수", value: "integer" },
      { label: "소수", value: "decimal" },
    ],
  });

  const stringTypeCollection = createListCollection({
    items: [
      { label: "짧은 텍스트", value: "shortText" },
      { label: "긴 텍스트", value: "longText" },
      { label: "이메일", value: "email" },
      { label: "전화번호", value: "phone" },
      { label: "주소", value: "address" },
    ],
  });

  const datetimeTypeCollection = createListCollection({
    items: [
      { label: "날짜+시간", value: "datetime" },
      { label: "날짜", value: "date" },
      { label: "시간", value: "time" },
    ],
  });

  const fileExtCollection = createListCollection({
    items: [
      { label: "PDF", value: "pdf" },
      { label: "PPT", value: "ppt" },
      { label: "JPG", value: "jpg" },
      { label: "XLSX", value: "xlsx" },
    ],
  });

  useEffect(() => {
    setLocalField(field);
    setSelectedFieldType(field.field_type);
    setNumberType(field.number_metadata?.number_type || "natural");
    setStringType(field.string_metadata?.string_type || "shortText");
    setDatetimeType(field.datetime_metadata?.datetime_type || "datetime");
    setSelectionOptions(
      (field.selection_metadata?.options || []).map(
        (opt: { [key: string]: unknown }) => ({
          label: String(opt.label || ""),
          color: String(opt.color || "#000000"),
          id: String(opt.id || `option-${Date.now()}-${Math.random()}`),
        })
      )
    );
    setFileExt(field.file_metadata?.allowed_extension || "pdf");
    setFileSize(field.file_metadata?.max_file_size_mb || 10);
  }, [field]);

  const handleChange = (
    key: keyof SelectedField,
    value: SelectedField[keyof SelectedField]
  ) => {
    setLocalField((prev: SelectedField) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    const updated = { ...localField, field_type: selectedFieldType };
    if (selectedFieldType === "number") {
      updated.number_metadata = {
        number_type: numberType,
        unit: localField.number_metadata?.unit || "",
        min_value: localField.number_metadata?.min_value,
        max_value: localField.number_metadata?.max_value,
        decimal_round: localField.number_metadata?.decimal_round,
      };
      updated.string_metadata = null;
      updated.datetime_metadata = null;
      updated.selection_metadata = null;
      updated.file_metadata = null;
    } else if (selectedFieldType === "string") {
      updated.string_metadata = { string_type: stringType };
      updated.number_metadata = null;
      updated.datetime_metadata = null;
      updated.selection_metadata = null;
      updated.file_metadata = null;
    } else if (selectedFieldType === "datetime") {
      updated.datetime_metadata = { datetime_type: datetimeType };
      updated.number_metadata = null;
      updated.string_metadata = null;
      updated.selection_metadata = null;
      updated.file_metadata = null;
    } else if (selectedFieldType === "selection") {
      updated.selection_metadata = {
        options: selectionOptions.map(({ label, color }) => ({ label, color })),
      };
      updated.number_metadata = null;
      updated.string_metadata = null;
      updated.datetime_metadata = null;
      updated.file_metadata = null;
    } else if (selectedFieldType === "file") {
      updated.file_metadata = {
        allowed_extension: fileExt,
        max_file_size_mb: fileSize,
      };
      updated.number_metadata = null;
      updated.string_metadata = null;
      updated.datetime_metadata = null;
      updated.selection_metadata = null;
    }
    onSave(updated);
    onClose();
  };

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={({ open }) => !open && onClose()}
      size={{ base: "xs", md: "lg" }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>필드 속성 편집</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack gap={4} align="stretch">
            <Field required label="필드명">
              <Input
                value={localField.mapped_name ?? localField.field_name}
                onChange={(e) => handleChange("mapped_name", e.target.value)}
                placeholder="필드명을 입력하세요"
              />
            </Field>
            <Field required label="설명">
              <Input
                value={localField.mapped_description ?? localField.description}
                onChange={(e) =>
                  handleChange("mapped_description", e.target.value)
                }
                placeholder="설명을 입력하세요"
              />
            </Field>
            <Field required label="필드 타입">
              <Grid templateColumns="repeat(4, 1fr)" gap={4}>
                {fieldTypes.map((ft) => (
                  <Box
                    key={ft.type}
                    onClick={() => setSelectedFieldType(ft.type as FieldType)}
                    p={3}
                    borderWidth={2}
                    borderColor={
                      selectedFieldType === ft.type ? "blue.500" : "gray.200"
                    }
                    borderRadius="md"
                    cursor="pointer"
                    textAlign="center"
                    _hover={{ borderColor: "blue.300" }}
                  >
                    <Icon as={ft.icon} w={5} h={5} />
                    <Text mt={2} fontSize="sm">
                      {ft.label}
                    </Text>
                  </Box>
                ))}
              </Grid>
            </Field>
            {/* 타입별 상세 속성 */}
            {selectedFieldType === "number" && (
              <>
                <Field required label="숫자 타입">
                  <Select.Root
                    value={[numberType]}
                    onValueChange={(details) =>
                      setNumberType(details.value[0] as typeof numberType)
                    }
                    name="numberType"
                    collection={numberTypeCollection}
                  >
                    <Select.HiddenSelect />
                    <Select.Control>
                      <Select.Trigger>
                        <Select.ValueText placeholder="숫자 유형 선택" />
                      </Select.Trigger>
                    </Select.Control>
                    <Select.Positioner>
                      <Select.Content>
                        {numberTypeCollection.items.map((item) => (
                          <Select.Item item={item} key={item.value}>
                            {item.label}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                </Field>
                <Field label="단위">
                  <Input
                    value={localField.number_metadata?.unit || ""}
                    onChange={(e) =>
                      setLocalField((prev: SelectedField) => ({
                        ...prev,
                        number_metadata: {
                          ...prev.number_metadata,
                          number_type:
                            prev.number_metadata?.number_type || "natural",
                          unit: e.target.value,
                        },
                      }))
                    }
                    placeholder="단위를 입력하세요 (예. kg, cm)"
                  />
                </Field>
                <Field label="최소값">
                  <Input
                    type="number"
                    value={localField.number_metadata?.min_value ?? ""}
                    onChange={(e) =>
                      setLocalField((prev: SelectedField) => ({
                        ...prev,
                        number_metadata: {
                          ...prev.number_metadata,
                          number_type:
                            prev.number_metadata?.number_type || "natural",
                          min_value: e.target.value
                            ? Number(e.target.value)
                            : undefined,
                        },
                      }))
                    }
                    placeholder="최소값을 입력하세요"
                  />
                </Field>
                <Field label="최대값">
                  <Input
                    type="number"
                    value={localField.number_metadata?.max_value ?? ""}
                    onChange={(e) =>
                      setLocalField((prev: SelectedField) => ({
                        ...prev,
                        number_metadata: {
                          ...prev.number_metadata,
                          number_type:
                            prev.number_metadata?.number_type || "natural",
                          max_value: e.target.value
                            ? Number(e.target.value)
                            : undefined,
                        },
                      }))
                    }
                    placeholder="최대값을 입력하세요"
                  />
                </Field>
                {numberType === "decimal" && (
                  <Field label="반올림 소수점">
                    <Input
                      type="number"
                      value={localField.number_metadata?.decimal_round ?? ""}
                      onChange={(e) =>
                        setLocalField((prev: SelectedField) => ({
                          ...prev,
                          number_metadata: {
                            ...prev.number_metadata,
                            number_type:
                              prev.number_metadata?.number_type || "natural",
                            decimal_round: e.target.value
                              ? Number(e.target.value)
                              : undefined,
                          },
                        }))
                      }
                      placeholder="반올림 소수점을 입력하세요"
                    />
                  </Field>
                )}
              </>
            )}
            {selectedFieldType === "string" && (
              <Field required label="문자 유형">
                <Select.Root
                  value={[stringType]}
                  onValueChange={(details) =>
                    setStringType(details.value[0] as typeof stringType)
                  }
                  name="stringType"
                  collection={stringTypeCollection}
                >
                  <Select.HiddenSelect />
                  <Select.Control>
                    <Select.Trigger>
                      <Select.ValueText placeholder="문자 유형 선택" />
                    </Select.Trigger>
                  </Select.Control>
                  <Select.Positioner>
                    <Select.Content>
                      {stringTypeCollection.items.map((item) => (
                        <Select.Item item={item} key={item.value}>
                          {item.label}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>
              </Field>
            )}
            {selectedFieldType === "datetime" && (
              <Field required label="날짜/시간 유형">
                <Select.Root
                  value={[datetimeType]}
                  onValueChange={(details) =>
                    setDatetimeType(details.value[0] as typeof datetimeType)
                  }
                  name="datetimeType"
                  collection={datetimeTypeCollection}
                >
                  <Select.HiddenSelect />
                  <Select.Control>
                    <Select.Trigger>
                      <Select.ValueText placeholder="날짜/시간 유형 선택" />
                    </Select.Trigger>
                  </Select.Control>
                  <Select.Positioner>
                    <Select.Content>
                      {datetimeTypeCollection.items.map((item) => (
                        <Select.Item item={item} key={item.value}>
                          {item.label}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>
              </Field>
            )}
            {selectedFieldType === "selection" && (
              <Field required label="선택 옵션">
                <VStack gap={2} align="stretch">
                  {selectionOptions.map(
                    (
                      opt: { label: string; color: string; id?: string },
                      idx: number
                    ) => (
                      <HStack key={opt.id || `option-${idx}`}>
                        <Input
                          value={opt.label || ""}
                          onChange={(e) => {
                            const newOpts = [...selectionOptions];
                            newOpts[idx] = {
                              ...newOpts[idx],
                              label: e.target.value,
                            };
                            setSelectionOptions(newOpts);
                          }}
                          placeholder="옵션명"
                        />
                        <Input
                          type="color"
                          value={opt.color || "#000000"}
                          onChange={(e) => {
                            const newOpts = [...selectionOptions];
                            newOpts[idx] = {
                              ...newOpts[idx],
                              color: e.target.value,
                            };
                            setSelectionOptions(newOpts);
                          }}
                          w="40px"
                          p={0}
                        />
                        <Button
                          size="xs"
                          onClick={() =>
                            setSelectionOptions(
                              selectionOptions.filter((_, i) => i !== idx)
                            )
                          }
                        >
                          삭제
                        </Button>
                      </HStack>
                    )
                  )}
                  <Button
                    size="sm"
                    onClick={() =>
                      setSelectionOptions([
                        ...selectionOptions,
                        {
                          label: "",
                          color: "#000000",
                          id: `option-${Date.now()}-${Math.random()}`,
                        },
                      ])
                    }
                  >
                    옵션 추가
                  </Button>
                </VStack>
              </Field>
            )}
            {selectedFieldType === "file" && (
              <>
                <Field required label="허용 확장자">
                  <Select.Root
                    value={[fileExt]}
                    onValueChange={(details) =>
                      setFileExt(details.value[0] as typeof fileExt)
                    }
                    name="fileExt"
                    collection={fileExtCollection}
                  >
                    <Select.HiddenSelect />
                    <Select.Control>
                      <Select.Trigger>
                        <Select.ValueText placeholder="허용 확장자 선택" />
                      </Select.Trigger>
                    </Select.Control>
                    <Select.Positioner>
                      <Select.Content>
                        {fileExtCollection.items.map((item) => (
                          <Select.Item item={item} key={item.value}>
                            {item.label}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                </Field>
                <Field required label="최대 파일 크기(MB)">
                  <Input
                    type="number"
                    value={fileSize}
                    onChange={(e) => setFileSize(Number(e.target.value))}
                    placeholder="최대 파일 크기(MB)"
                  />
                </Field>
              </>
            )}
          </VStack>
        </DialogBody>
        <DialogFooter>
          <HStack w="100%" justify="flex-end">
            <Button variant="subtle" onClick={onClose}>
              취소
            </Button>
            <Button variant="solid" onClick={handleSave}>
              저장
            </Button>
          </HStack>
        </DialogFooter>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  );
};

export default TempFieldEditDialog;
